"use client";

import { useEffect, useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import Image from "next/image";
import dynamic from "next/dynamic";
const ReactPlayer = dynamic(() => import("react-player"));
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
const CancelIcon = dynamic(() => import("@mui/icons-material/Cancel"));
import { sbBlok } from "@/types/sb-types";
import Cta from "./Cta";
import {
  NODE_HEADING,
  NODE_PARAGRAPH,
  NODE_UL,
  NODE_OL,
  NODE_LI,
  render,
} from "storyblok-rich-text-react-renderer";
import clsx from "clsx";

interface VideoComponentProps {
  blok: sbBlok;
}

const VideoComponent = ({ blok }: VideoComponentProps) => {
  const { video_thumbnail, video_source, is_portrait } = blok;
  const [showVideo, setShowVideo] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isModalOpen]);

  return (
    <section {...storyblokEditable(blok)}>
      <div
        className={clsx("grid-container", {
          "pt-7 lg:pt-14":
            !!blok.spacing_top?.length &&
            blok.spacing_top[0].option === "large",
          "pt-14 lg:pt-[120px]":
            !!blok.spacing_top?.length &&
            blok.spacing_top[0].option === "x_large",
          "pt-[120px] lg:pt-[180px]":
            !!blok.spacing_top?.length &&
            blok.spacing_top[0].option === "xx_large",
          "pb-7 lg:pb-14":
            !!blok.spacing_bottom?.length &&
            blok.spacing_bottom[0].option === "large",
          "pb-14 lg:pb-[120px]":
            !!blok.spacing_bottom?.length &&
            blok.spacing_bottom[0].option === "x_large",
          "pb-[120px] lg:pb-[180px]":
            !!blok.spacing_bottom?.length &&
            blok.spacing_bottom[0].option === "xx_large",
        })}
      >
        <div className="col-start-1 col-span-4 row-start-2 lg:col-start-2 lg:col-span-8 lg:row-start-1 2xl:col-start-5 2xl:col-span-13">
          <div
            className={clsx(
              "block lg:hidden group",
              "h-[200px] flex items-center justify-center relative cursor-pointer rounded-[10px] overflow-hidden",
              "min-[531px]:h-[300px] min-[751px]:h-[350px] xl:h-[400px] min-[1601px]:h-[450px]",
              {
                "h-[450px] w-4/5 m-auto min-[531px]:h-[500px] min-[531px]:w-[350px]  min-[751px]:h-[500px]":
                  is_portrait,
              }
            )}
          >
            {!showVideo ? (
              <>
                <div
                  className="absolute inset-0 z-[1] opacity-100 transition-opacity duration-300 pointer-events-none 
           bg-[radial-gradient(circle,rgba(255,255,255,0)_0%,rgba(0,0,0,0.8)_100%)] 
           group-hover:opacity-60"
                />
                {video_thumbnail?.filename && (
                  <Image
                    onClick={() => setShowVideo(true)}
                    src={video_thumbnail?.filename}
                    sizes="100vw"
                    alt={video_thumbnail?.alt}
                    fill
                    className="object-cover"
                  />
                )}
                <PlayArrowIcon
                  className="group-hover:text-[rgba(255,255,255,0.3)] hover:text-[rgba(255,255,255,0.3)] text-[rgba(255,255,255,0.6)] z-[1] cursor-pointer transition-colors duration-300"
                  style={{
                    fontSize: 72,
                  }}
                  onClick={() => setShowVideo(true)}
                />
              </>
            ) : (
              <ReactPlayer
                url={video_source}
                playing
                playsinline
                controls
                width="100%"
                height="100%"
                config={{
                  vimeo: {
                    playerOptions: {
                      autoplay: true,
                      playsinline: true,
                    },
                  },
                }}
              />
            )}
          </div>
          <div
            className={`group hidden lg:flex
              relative items-center justify-center
              h-[200px] cursor-pointer rounded-[10px] overflow-hidden
              min-[531px]:!h-[300px] min-[751px]:!h-[350px] xl:h-[400px] min-[1601px]:h-[450px]
            `}
          >
            <div
              className="absolute inset-0 z-10 opacity-100 transition-opacity duration-300 pointer-events-none 
      bg-[radial-gradient(circle,rgba(255,255,255,0)_0%,rgba(0,0,0,0.8)_100%)] 
      group-hover:opacity-60" />
            {video_thumbnail?.filename && (
              <Image
                onClick={() => {
                  setIsModalOpen(true);
                  setShowVideo(true);
                }}
                src={video_thumbnail.filename}
                sizes="100vw"
                alt={video_thumbnail.alt}
                fill
                className="object-cover"
              />
            )}
            {!isModalOpen && (
              <PlayArrowIcon
                className="group-hover:text-[rgba(255,255,255,0.3)] hover:text-[rgba(255,255,255,0.3)] text-[rgba(255,255,255,0.6)] z-[1] cursor-pointer transition-colors duration-300"
                style={{ fontSize: 92 }}
                onClick={() => {
                  setIsModalOpen(true);
                  setShowVideo(true);
                }}
              />
            )}
            {isModalOpen && (
              <>
                <div
                  className="fixed inset-0 bg-[rgba(0,0,0,0.9)] z-[2]"
                  onClick={() => {
                    setIsModalOpen(false);
                    setShowVideo(false);
                  }}
                />
                <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-auto bg-[rgba(0,0,0,0.8)] z-10 [&>div]:relative [&>div]:pt-[56.25%] [&>div>div]:absolute [&>div>div]:top-0 [&>div>div]:left-0 [&>div>div]:w-full [&>div>div]:h-full">
                  <CancelIcon
                    className="absolute -top-7 -right-7 z-[2] text-[rgba(255,255,255,0.8)] cursor-pointer transition-colors duration-300"
                    style={{ fontSize: 36 }}
                    onClick={() => {
                      setIsModalOpen(false);
                      setShowVideo(false);
                    }}
                  />
                  <ReactPlayer
                    url={video_source}
                    playing
                    controls
                    width="100%"
                    height="auto"
                    config={{
                      vimeo: {
                        playerOptions: { playsinline: true },
                      },
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="col-start-1 col-span-4 row-start-1 lg:col-start-11 lg:col-span-5 2xl:col-start-20 2xl:col-span-8 flex mx-auto my-0">
          <div className="mt-auto mx-0 mb-10 text-center lg:text-left lg:my-auto">
            {blok.content_text &&
              render(blok.content_text, {
                nodeResolvers: {
                  [NODE_PARAGRAPH]: (children) => (
                    <p className="mt-0 mx-0 mb-5 last-of-type:m-0">
                      {children}
                    </p>
                  ),
                  [NODE_HEADING]: (
                    children,
                    props: { level: 1 | 2 | 3 | 4 | 5 | 6 }
                  ) => {
                    if (props?.level === 1)
                      return <h1 className="mt-0 mx-0 mb-5">{children}</h1>;

                    if (props?.level === 2)
                      return <h2 className="mt-0 mx-0 mb-5">{children}</h2>;

                    return <span>{children}</span>;
                  },
                  [NODE_UL]: (children) => (
                    <ul className="pl-16 list-disc">{children}</ul>
                  ),
                  [NODE_OL]: (children) => (
                    <ol className="pl-16 list-decimal">{children}</ol>
                  ),
                  [NODE_LI]: (children) => (
                    <li className="mb-6 last:mb-0">{children}</li>
                  ),
                },
                blokResolvers: {
                  ["cta_button"]: (props: any) => {
                    return (
                      <Cta
                        key={props?._uid}
                        name={
                          props?.name?.length > 0
                            ? props?.name
                            : props?.link?.story?.name
                        }
                        href={props?.link?.url}
                        fullSlug={props?.link?.story?.full_slug}
                        className="mt-5"
                      />
                    );
                  },
                },
              })}
            {blok.content.length > 0 &&
              blok.content.map((c: sbBlok) => {
                if (c.component === "paragraph") {
                  return (
                    <p key={c._uid} className="mt-0 mx-0 mb-5 last-of-type:m-0">
                      {c.content}
                    </p>
                  );
                }
                if (c.component === "cta_button") {
                  return (
                    <Cta
                      className="mx-auto mt-5 lg:mx-0"
                      key={c._uid}
                      name={c.name.length > 0 ? c.name : c.link.story?.name}
                      href={c.link?.url}
                      fullSlug={c.link?.story?.full_slug}
                    />
                  );
                }
              })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoComponent;
